import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { MMKV } from 'react-native-mmkv';
import { createScopedLogger } from '@/utils/logging/createScopedLogger';

const logger = createScopedLogger('UnifiedStartupCache');
const logWithTimestamp = (msg: string, ...args: unknown[]) => logger.info(msg, ...args);
const errorWithTimestamp = (msg: string, ...args: unknown[]) => logger.error(msg, ...args);

/**
 * Unified Startup Cache Service
 *
 * Consolidates all critical startup data into a single cache to minimize
 * storage I/O operations and enable parallel provider initialization.
 *
 * Uses MMKV for SYNCHRONOUS reads on native platforms (iOS/Android)
 * Falls back to AsyncStorage on web.
 *
 * Cache includes:
 * - Country detection result
 * - Campaign configuration
 * - Translation preferences
 * - Last successful fetch timestamps
 */

const CACHE_KEY = '@unified_startup_cache_v4'; // v4: Added auth credentials cache
const MMKV_CACHE_KEY = 'unified_startup_cache_v4';
const CACHE_TTL = 10 * 60 * 1000;

const mmkvStorage = Platform.OS !== 'web' ? new MMKV({ id: 'startup-cache' }) : null;

export interface CampaignConfigData {
  id: string;
  campaign_id: string;
  country_code: string;
  language_code: string;
  culture: string | null;
  free_episode_price_coins: number;
  fallback_language_code: string | null;
  free_episodes_count: number | null;
  revenue_cat_enabled_app?: boolean;
  revenue_cat_enabled_web?: boolean;
  dcb_enabled_web?: boolean;
}

export interface UserPreferencesData {
  interfaceLanguage: string | null;
  subtitleLanguage: string | null;
  isPaying: boolean;
  timestamp: number;
  smartuserId: string | null;
}

export interface AuthCacheData {
  token: string;
  userJson: string;
  smartuserId: string;
  timestamp: number;
}

export interface StartupCacheData {
  // Country data
  countryCode: string;
  countryName: string;
  countryDetectionMethod: 'geoip' | 'fallback';
  countryTimestamp: number;

  // Campaign configuration data
  allCampaignConfigs: CampaignConfigData[];
  frenchFallbackId: string | null;
  availableLanguages: string[];
  campaignTimestamp: number;

  // Translation data
  userPreferredLanguage: string;
  translationTimestamp: number;

  // Subtitle preference data (legacy - kept for backward compatibility)
  subtitlePreference: string | null;
  subtitlePreferenceTimestamp: number;
  subtitlePreferenceSmartuserId: string | null;

  // Consolidated user preferences (NEW in v3)
  userPreferences: UserPreferencesData | null;

  // Auth credentials cache (NEW in v4)
  authCache: AuthCacheData | null;
  authLoggedOut: boolean;

  // Full-prefetch tracking
  lastFullPrefetchTimestamp: number;
  lastFullPrefetchCampaignId: string | null;

  // Cache metadata
  version: string;
  createdAt: number;
}

class UnifiedStartupCacheService {
  private cache: StartupCacheData | null = null;
  private initialized = false;
  private campaignIdHistory: Map<string, number> = new Map();
  private currentCampaignId: string | null = null;

  constructor() {
    this.initializeSync();
  }

  /**
   * SYNCHRONOUS initialization from MMKV (native only)
   * This runs immediately when the service is instantiated
   */
  private initializeSync(): void {
    if (Platform.OS === 'web' || !mmkvStorage) {
      return;
    }

    const startTime = Date.now();
    try {
      const cached = mmkvStorage.getString(MMKV_CACHE_KEY);
      if (cached) {
        const data = JSON.parse(cached) as StartupCacheData;
        const age = Date.now() - data.createdAt;

        if (age < CACHE_TTL) {
          this.cache = data;
          this.initialized = true;
          console.log(`🚀 [StartupCache] ✅ SYNC loaded cache from MMKV (age: ${Math.round(age / 60000)}m, ${Date.now() - startTime}ms)`);
        } else {
          console.log(`🚀 [StartupCache] ⏰ MMKV cache expired (age: ${Math.round(age / 60000)}m)`);
          mmkvStorage.delete(MMKV_CACHE_KEY);
        }
      } else {
        console.log('🚀 [StartupCache] No MMKV cache found');
      }
    } catch (error) {
      console.error('🚀 [StartupCache] Error in sync initialization:', error);
    }
  }

  /**
   * Get cache data SYNCHRONOUSLY (for initial render)
   * Returns null on web or if cache is not available
   */
  getSync(): StartupCacheData | null {
    return this.cache;
  }

  /**
   * Check if cache is ready SYNCHRONOUSLY
   */
  isReady(): boolean {
    return this.cache !== null;
  }

  /**
   * Initialize and load cache from storage (async fallback for web)
   */
  async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }

    const startTime = Date.now();
    logWithTimestamp('🚀 [StartupCache] Initializing unified startup cache...');

    try {
      if (Platform.OS !== 'web' && mmkvStorage) {
        const cached = mmkvStorage.getString(MMKV_CACHE_KEY);
        if (cached) {
          const data = JSON.parse(cached) as StartupCacheData;
          const age = Date.now() - data.createdAt;

          if (age < CACHE_TTL) {
            this.cache = data;
            const ageMinutes = Math.round(age / 60000);
            logWithTimestamp(`🚀 [StartupCache] ✅ Loaded cache from MMKV (age: ${ageMinutes}m, ${Date.now() - startTime}ms)`);
          } else {
            logWithTimestamp(`🚀 [StartupCache] ⏰ Cache expired (age: ${Math.round(age / 60000)}m), will refresh`);
            await this.clear();
          }
        } else {
          logWithTimestamp('🚀 [StartupCache] No cache found, first launch or cache cleared');
        }
      } else {
        const cached = await AsyncStorage.getItem(CACHE_KEY);

        if (cached) {
          const data = JSON.parse(cached) as StartupCacheData;
          const age = Date.now() - data.createdAt;

          if (age < CACHE_TTL) {
            this.cache = data;
            const ageMinutes = Math.round(age / 60000);
            logWithTimestamp(`🚀 [StartupCache] ✅ Loaded cache from AsyncStorage (age: ${ageMinutes}m, ${Date.now() - startTime}ms)`);
          } else {
            logWithTimestamp(`🚀 [StartupCache] ⏰ Cache expired (age: ${Math.round(age / 60000)}m), will refresh`);
            await this.clear();
          }
        } else {
          logWithTimestamp('🚀 [StartupCache] No cache found, first launch or cache cleared');
        }
      }

      this.initialized = true;
    } catch (error) {
      errorWithTimestamp('🚀 [StartupCache] Error loading cache:', error);
      this.cache = null;
      this.initialized = true;
    }
  }

  /**
   * Get cached startup data
   */
  async get(): Promise<StartupCacheData | null> {
    await this.initialize();
    return this.cache;
  }

  /**
   * Check if cache exists and is valid
   */
  async hasValidCache(): Promise<boolean> {
    await this.initialize();
    return this.cache !== null;
  }

  /**
   * Save data to storage (MMKV on native, AsyncStorage on web)
   */
  private async saveToStorage(): Promise<void> {
    if (!this.cache) return;
    const json = JSON.stringify(this.cache);

    if (Platform.OS !== 'web' && mmkvStorage) {
      mmkvStorage.set(MMKV_CACHE_KEY, json);
    } else {
      await AsyncStorage.setItem(CACHE_KEY, json);
    }
  }

  /**
   * Set complete startup cache data
   */
  async set(data: StartupCacheData): Promise<void> {
    const startTime = Date.now();

    try {
      data.createdAt = Date.now();
      data.version = 'v4';

      this.cache = data;
      await this.saveToStorage();

      logWithTimestamp(`🚀 [StartupCache] ✅ Cache saved (${Date.now() - startTime}ms)`);
    } catch (error) {
      errorWithTimestamp('🚀 [StartupCache] Error saving cache:', error);
    }
  }

  /**
   * Update country data in cache
   */
  async updateCountryData(
    countryCode: string,
    countryName: string,
    method: 'geoip' | 'fallback'
  ): Promise<void> {
    await this.initialize();

    if (!this.cache) {
      this.cache = {
        countryCode,
        countryName,
        countryDetectionMethod: method,
        countryTimestamp: Date.now(),
        allCampaignConfigs: [],
        frenchFallbackId: null,
        availableLanguages: [],
        campaignTimestamp: 0,
        userPreferredLanguage: 'en',
        translationTimestamp: 0,
        subtitlePreference: null,
        subtitlePreferenceTimestamp: 0,
        subtitlePreferenceSmartuserId: null,
        userPreferences: null,
        authCache: null,
        authLoggedOut: false,
        lastFullPrefetchTimestamp: 0,
        lastFullPrefetchCampaignId: null,
        version: 'v4',
        createdAt: Date.now(),
      };
    } else {
      this.cache.countryCode = countryCode;
      this.cache.countryName = countryName;
      this.cache.countryDetectionMethod = method;
      this.cache.countryTimestamp = Date.now();
    }

    await this.saveToStorage();
  }

  /**
   * Update campaign configuration data in cache
   */
  async updateCampaignData(
    allConfigs: CampaignConfigData[],
    frenchFallbackId: string | null,
    availableLanguages: string[]
  ): Promise<void> {
    await this.initialize();

    if (!this.cache) {
      logWithTimestamp('🚀 [StartupCache] Cannot update campaign data, cache not initialized');
      return;
    }

    this.cache.allCampaignConfigs = allConfigs;
    this.cache.frenchFallbackId = frenchFallbackId;
    this.cache.availableLanguages = availableLanguages;
    this.cache.campaignTimestamp = Date.now();

    await this.saveToStorage();
    logWithTimestamp('🚀 [StartupCache] Campaign data updated in cache');
  }

  /**
   * Update translation preference in cache
   */
  async updateTranslationData(language: string): Promise<void> {
    await this.initialize();

    if (!this.cache) {
      logWithTimestamp('🚀 [StartupCache] Cannot update translation data, cache not initialized');
      return;
    }

    this.cache.userPreferredLanguage = language;
    this.cache.translationTimestamp = Date.now();

    await this.saveToStorage();
    logWithTimestamp('🚀 [StartupCache] Translation data updated in cache');
  }

  /**
   * Clear the cache
   */
  async clear(): Promise<void> {
    try {
      if (Platform.OS !== 'web' && mmkvStorage) {
        mmkvStorage.delete(MMKV_CACHE_KEY);
      } else {
        await AsyncStorage.removeItem(CACHE_KEY);
      }
      this.cache = null;
      logWithTimestamp('🚀 [StartupCache] Cache cleared');
    } catch (error) {
      errorWithTimestamp('🚀 [StartupCache] Error clearing cache:', error);
    }
  }

  /**
   * Get cache age in milliseconds
   */
  getCacheAge(): number | null {
    if (!this.cache) {
      return null;
    }
    return Date.now() - this.cache.createdAt;
  }

  /**
   * Check if specific data in cache needs refresh
   * Note: Event-based refresh strategy - only refresh on explicit triggers
   */
  needsRefresh(dataType: 'country' | 'campaign' | 'translation' | 'subtitle'): boolean {
    if (!this.cache) {
      return true;
    }

    // Event-based refresh - never expire automatically
    // Data is refreshed only when:
    // 1. User changes language/settings
    // 2. App returns from background
    // 3. Explicit refresh is requested
    return false;
  }

  async needsFullPrefetch(campaignId: string): Promise<boolean> {
    await this.initialize();

    if (!this.cache) {
      logWithTimestamp('🚀 [StartupCache] Full-prefetch needed: No cache exists');
      return true;
    }

    // Check if campaign changed
    if (this.cache.lastFullPrefetchCampaignId !== campaignId) {
      logWithTimestamp(`🚀 [StartupCache] Full-prefetch needed: Campaign changed from ${this.cache.lastFullPrefetchCampaignId} to ${campaignId}`);
      return true;
    }

    // Check if last prefetch was more than 24 hours ago
    if (this.cache.lastFullPrefetchTimestamp === 0) {
      logWithTimestamp('🚀 [StartupCache] Full-prefetch needed: Never performed before');
      return true;
    }

    const age = Date.now() - this.cache.lastFullPrefetchTimestamp;

    if (age > CACHE_TTL) {
      const ageMinutes = Math.round(age / (60 * 1000));
      logWithTimestamp(`🚀 [StartupCache] Full-prefetch needed: Last prefetch was ${ageMinutes}min ago (threshold: 10min)`);
      return true;
    }

    const ageMinutes = Math.round(age / (60 * 1000));
    logWithTimestamp(`🚀 [StartupCache] Full-prefetch NOT needed: Last prefetch was ${ageMinutes}min ago (valid for 10min)`);
    return false;
  }

  async markFullPrefetchComplete(campaignId: string): Promise<void> {
    await this.initialize();

    if (!this.cache) {
      logWithTimestamp('🚀 [StartupCache] Cannot mark full-prefetch complete, cache not initialized');
      return;
    }

    this.cache.lastFullPrefetchTimestamp = Date.now();
    this.cache.lastFullPrefetchCampaignId = campaignId;

    await this.saveToStorage();
    logWithTimestamp(`🚀 [StartupCache] Full-prefetch marked as complete for campaign ${campaignId}`);
  }

  isCampaignStable(campaignId: string): boolean {
    const timestamp = new Date().toISOString();

    if (!campaignId) {
      logWithTimestamp(`[${timestamp}] 🚀 [StartupCache] Campaign stability check: NO campaign ID`);
      return false;
    }

    if (this.currentCampaignId !== campaignId) {
      logWithTimestamp(`[${timestamp}] 🚀 [StartupCache] Campaign changed from ${this.currentCampaignId} to ${campaignId}`);
      this.currentCampaignId = campaignId;
      this.campaignIdHistory.set(campaignId, Date.now());
      return false;
    }

    const firstSeenTime = this.campaignIdHistory.get(campaignId);
    if (!firstSeenTime) {
      logWithTimestamp(`[${timestamp}] 🚀 [StartupCache] First time seeing campaign ${campaignId}`);
      this.campaignIdHistory.set(campaignId, Date.now());
      return false;
    }

    const stabilityDuration = Date.now() - firstSeenTime;
    const isStable = stabilityDuration >= 500;

    logWithTimestamp(
      `[${timestamp}] 🚀 [StartupCache] Campaign ${campaignId} stability: ${stabilityDuration}ms (threshold: 500ms) - ${isStable ? 'STABLE' : 'NOT STABLE'}`
    );

    return isStable;
  }

  validateCampaignStability(campaignId: string): boolean {
    if (this.currentCampaignId !== campaignId) {
      const timestamp = new Date().toISOString();
      logWithTimestamp(
        `[${timestamp}] 🚀 [StartupCache] ⚠️ Campaign changed during operation! Expected: ${this.currentCampaignId}, Got: ${campaignId}`
      );
      return false;
    }
    return true;
  }

  resetCampaignStability(): void {
    const timestamp = new Date().toISOString();
    logWithTimestamp(`[${timestamp}] 🚀 [StartupCache] Resetting campaign stability tracking`);
    this.currentCampaignId = null;
    this.campaignIdHistory.clear();
  }

  /**
   * Update subtitle preference in cache (NEW in v2)
   */
  async updateSubtitlePreference(
    smartuserId: string,
    language: string | null
  ): Promise<void> {
    await this.initialize();

    if (!this.cache) {
      logWithTimestamp('🚀 [StartupCache] Cannot update subtitle preference, cache not initialized');
      return;
    }

    this.cache.subtitlePreference = language;
    this.cache.subtitlePreferenceTimestamp = Date.now();
    this.cache.subtitlePreferenceSmartuserId = smartuserId;

    await this.saveToStorage();
    logWithTimestamp(`🚀 [StartupCache] Subtitle preference updated for user ${smartuserId}: ${language || 'disabled'}`);
  }

  /**
   * Get subtitle preference from cache (NEW in v2)
   */
  getSubtitlePreference(smartuserId: string): string | null {
    if (!this.cache || this.cache.subtitlePreferenceSmartuserId !== smartuserId) {
      return null;
    }
    return this.cache.subtitlePreference;
  }

  /**
   * Clear subtitle preference (typically on logout)
   */
  async clearSubtitlePreference(): Promise<void> {
    await this.initialize();

    if (!this.cache) {
      return;
    }

    this.cache.subtitlePreference = null;
    this.cache.subtitlePreferenceTimestamp = 0;
    this.cache.subtitlePreferenceSmartuserId = null;

    await this.saveToStorage();
    logWithTimestamp('🚀 [StartupCache] Subtitle preference cleared');
  }

  /**
   * Update consolidated user preferences (NEW in v3)
   * Single method to store all user preferences loaded from DB
   */
  async updateUserPreferences(
    smartuserId: string,
    preferences: {
      interfaceLanguage: string | null;
      subtitleLanguage: string | null;
      isPaying: boolean;
    }
  ): Promise<void> {
    await this.initialize();

    if (!this.cache) {
      logWithTimestamp('🚀 [StartupCache] Cannot update user preferences, cache not initialized');
      return;
    }

    this.cache.userPreferences = {
      interfaceLanguage: preferences.interfaceLanguage,
      subtitleLanguage: preferences.subtitleLanguage,
      isPaying: preferences.isPaying,
      timestamp: Date.now(),
      smartuserId,
    };

    // Also update legacy subtitle fields for backward compatibility
    this.cache.subtitlePreference = preferences.subtitleLanguage;
    this.cache.subtitlePreferenceTimestamp = Date.now();
    this.cache.subtitlePreferenceSmartuserId = smartuserId;

    await this.saveToStorage();
    logWithTimestamp(`🚀 [StartupCache] User preferences updated for user ${smartuserId}`);
  }

  /**
   * Get consolidated user preferences from cache (NEW in v3)
   * Returns null if not cached or if cached for different user
   */
  getUserPreferences(smartuserId: string): UserPreferencesData | null {
    if (!this.cache || this.cache.userPreferences?.smartuserId !== smartuserId) {
      return null;
    }
    return this.cache.userPreferences;
  }

  /**
   * Check if user preferences are loaded for a specific user (NEW in v3)
   */
  hasUserPreferences(smartuserId: string): boolean {
    return (
      this.cache !== null &&
      this.cache.userPreferences !== null &&
      this.cache.userPreferences.smartuserId === smartuserId
    );
  }

  /**
   * Clear user preferences (typically on logout) (NEW in v3)
   */
  async clearUserPreferences(): Promise<void> {
    await this.initialize();

    if (!this.cache) {
      return;
    }

    this.cache.userPreferences = null;
    // Also clear legacy subtitle fields
    this.cache.subtitlePreference = null;
    this.cache.subtitlePreferenceTimestamp = 0;
    this.cache.subtitlePreferenceSmartuserId = null;

    await this.saveToStorage();
    logWithTimestamp('🚀 [StartupCache] User preferences cleared');
  }

  /**
   * Get cache statistics for monitoring
   */
  getStats(): {
    cacheAge: number | null;
    hasCache: boolean;
    campaignConfigCount: number;
    subtitleUser: string | null;
    subtitleLanguage: string | null;
    userPreferencesLoaded: boolean;
    userPreferencesUser: string | null;
  } {
    if (!this.cache) {
      return {
        cacheAge: null,
        hasCache: false,
        campaignConfigCount: 0,
        subtitleUser: null,
        subtitleLanguage: null,
        userPreferencesLoaded: false,
        userPreferencesUser: null,
      };
    }

    return {
      cacheAge: this.getCacheAge(),
      hasCache: true,
      campaignConfigCount: this.cache.allCampaignConfigs.length,
      subtitleUser: this.cache.subtitlePreferenceSmartuserId,
      subtitleLanguage: this.cache.subtitlePreference,
      userPreferencesLoaded: this.cache.userPreferences !== null,
      userPreferencesUser: this.cache.userPreferences?.smartuserId || null,
    };
  }

  async setAuthCredentials(
    token: string,
    userJson: string,
    smartuserId: string
  ): Promise<void> {
    await this.initialize();

    if (!this.cache) {
      logWithTimestamp('🚀 [StartupCache] Cannot set auth credentials, cache not initialized');
      return;
    }

    this.cache.authCache = {
      token,
      userJson,
      smartuserId,
      timestamp: Date.now(),
    };
    this.cache.authLoggedOut = false;

    await this.saveToStorage();
    logWithTimestamp(`🚀 [StartupCache] Auth credentials cached for user ${smartuserId}`);
  }

  getAuthCredentials(): AuthCacheData | null {
    if (!this.cache || this.cache.authLoggedOut) {
      return null;
    }
    return this.cache.authCache;
  }

  async setAuthLoggedOut(loggedOut: boolean): Promise<void> {
    await this.initialize();

    if (!this.cache) {
      return;
    }

    this.cache.authLoggedOut = loggedOut;
    if (loggedOut) {
      this.cache.authCache = null;
    }

    await this.saveToStorage();
    logWithTimestamp(`🚀 [StartupCache] Auth logged out flag set to: ${loggedOut}`);
  }

  isAuthLoggedOut(): boolean {
    return this.cache?.authLoggedOut ?? false;
  }

  async clearAuthCredentials(): Promise<void> {
    await this.initialize();

    if (!this.cache) {
      return;
    }

    this.cache.authCache = null;
    this.cache.authLoggedOut = true;

    await this.saveToStorage();
    logWithTimestamp('🚀 [StartupCache] Auth credentials cleared');
  }
}

export const unifiedStartupCache = new UnifiedStartupCacheService();
